import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { GetUserDto } from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import { DueDates } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/dueDate.interface';
import {
    AumicoCoreProvider,
    IAumMenuOptions,
    INotificationData,
    NotificationType,
} from '../../../../../../../../aumico-lib-core-fe/src/lib';
import { Comment, FcpTreeItemType } from '../../domain/models/fcp.model';
import { FcpTreeItem } from '../../domain/models/fcpTreeItem.model';
import { addComment, deleteComment, updateComment } from '../../domain/reducers/fcp.actions';
import { FcpFeatureState, selectActiveTestItem } from '../../domain/reducers/fcp.reducer';
const COPY_NOTIFICATION_DISPLAY_TIME_IN_MILLI_SEC = 2000;

@UntilDestroy()
@Component({
    selector: 'app-fcp-comments',
    templateUrl: 'fcpComments.component.html',
    styleUrls: ['fcpComments.component.scss'],
})
export class FcpCommentsComponent implements OnInit {
    @Input() lastYear: boolean = false;
    @Input() dueDates: DueDates;
    @Input() currentUser: GetUserDto;
    FcpTreeItemType = FcpTreeItemType; // Make enum accessible in template
    item$: Observable<FcpTreeItem>;
    comments$ = new BehaviorSubject<Comment[]>([]);
    commentForm: FormGroup;
    isTextareaFocused: boolean = false;
    isMenuOpenIndex: number | null = null;
    menuPosition = { top: 0, right: 0 };
    editingCommentId: string | null = null; // Track the ID of the comment being edited

    OPTIONS: IAumMenuOptions = {
        items: [
            {
                caption: this.localizedTexts?.t('aumico_button_delete') as string,
                action: 'DELETE',
            },
        ],
    };
    showToast = false;

    constructor(
        private readonly store: Store<FcpFeatureState>,
        private fb: FormBuilder,
        private readonly localizedTexts: I18NextService,
        private readonly aumicoCoreProvider: AumicoCoreProvider,
    ) {}

    ngOnInit() {
        this.initializeForm();
        this.item$ = this.store.select(selectActiveTestItem).pipe(untilDestroyed(this));

        this.item$
            .pipe(
                untilDestroyed(this),
                map((item) => {
                    const relevantDueDate = this.lastYear ? this.dueDates.dueDatePY : this.dueDates.dueDate;
                    return (item?.comments || [])
                        .filter(
                            (comment) => new Date(comment.dueDate).getTime() === new Date(relevantDueDate).getTime(),
                        )
                        .sort((b, a) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                }),
            )
            .subscribe((comments) => this.comments$.next(comments));
    }

    private initializeForm() {
        this.commentForm = this.fb.group({
            text: [''],
            isPendency: [false],
            isExternal: [false],
            isInternal: [false],
            needsReview: [false],
        });
    }

    addComment() {
        if (this.commentForm.invalid || !this.commentForm.get('text')?.value?.trim()) {
            return;
        }
        const newComment: Comment = {
            id: this.editingCommentId ? this.editingCommentId : uuidV4(), // Use existing ID if editing
            createdAt: new Date(),
            createdBy: this.currentUser.id,
            createdByName: this.currentUser.firstName + ' ' + this.currentUser.lastName,
            text: this.commentForm.value.text,
            isPendency: this.commentForm.value.isPendency,
            isExternal: this.commentForm.value.isExternal,
            isInternal: this.commentForm.value.isInternal,
            needsReview: this.commentForm.value.needsReview,
            dueDate: this.dueDates.dueDate,
        };

        if (this.editingCommentId) {
            this.store.dispatch(updateComment({ payload: newComment }));
        } else {
            this.store.dispatch(addComment({ payload: newComment }));
        }

        this.commentForm.reset(); // Reset the form after submission
        this.isTextareaFocused = false;
        this.editingCommentId = null; // Clear the editing ID after submission
    }

    onTextareaFocus() {
        this.isTextareaFocused = true;
    }

    onTextareaBlur() {
        if (!this.commentForm.value.text) {
            this.cancel();
        }
    }

    cancel() {
        this.commentForm.reset();
        this.isTextareaFocused = false;
        this.editingCommentId = null; // Clear the editing ID when canceling
    }

    copyToClipboard(text: string) {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                this.showCopyToastMessage();
            })
            .catch((err) => {
                console.error('Could not copy text: ', err);
            });
    }

    showCopyToastMessage(notificationData?: INotificationData): void {
        if (notificationData == null) {
            notificationData = {
                descriptionKey: 'aumico_report_copy_success_notification_description',
            };
        }

        this.aumicoCoreProvider.dispatchAddNotification(
            notificationData.titleKey,
            notificationData.descriptionKey,
            NotificationType.SUCCESS,
            COPY_NOTIFICATION_DISPLAY_TIME_IN_MILLI_SEC,
        );
    }

    toggleCommentMenu(event: MouseEvent, index: number): void {
        event.stopPropagation();
        this.isMenuOpenIndex = this.isMenuOpenIndex === index ? null : index;

        // Get the button element
        const buttonElement = event.target as HTMLElement;

        // Get the bounding rectangle of the button element
        const rect = buttonElement.getBoundingClientRect();

        // Calculate the position of the dropdown at the bottom right of the button
        const right = window.innerWidth - rect.right;
        const top = rect.bottom;

        // Set the menu position
        this.menuPosition = { top: top, right: right };
    }

    closeCommentMenu(): void {
        this.isMenuOpenIndex = null;
    }

    editComment(comment: Comment): void {
        this.closeCommentMenu(); // Close the menu
        this.editingCommentId = comment.id; // Set the ID of the comment being edited
        this.commentForm.patchValue({
            text: comment.text,
            isPendency: comment.isPendency,
            isExternal: comment.isExternal,
            isInternal: comment.isInternal,
            needsReview: comment.needsReview,
        });
        this.isTextareaFocused = true; // Ensure the textarea is focused
    }

    markAsInternal(value: boolean, comment: Comment): void {
        const updatedComment = {
            ...comment,
            isInternal: value,
        };
        this.store.dispatch(updateComment({ payload: updatedComment }));
    }

    markAsExternal(value: boolean, comment: Comment): void {
        const updatedComment = {
            ...comment,
            isExternal: value,
        };
        this.store.dispatch(updateComment({ payload: updatedComment }));
    }

    markAsPending(value: boolean, comment: Comment): void {
        const updatedComment = {
            ...comment,
            isPendency: value,
        };
        this.store.dispatch(updateComment({ payload: updatedComment }));
    }

    markAsNeedsReview(value: boolean, comment: Comment): void {
        const updatedComment = {
            ...comment,
            needsReview: value,
        };
        this.store.dispatch(updateComment({ payload: updatedComment }));
    }

    deleteComment(comment: Comment) {
        this.store.dispatch(deleteComment({ payload: comment }));
        this.isMenuOpenIndex = null;
    }
}
